import { SeriesPoint } from '../../../api/adminDashboard';

export const CHART_BLUE = '#4C7DF0';
export const CHART_TEAL = '#57D2DC';
export const CHART_BLUE_FILL = 'rgba(76, 125, 240, 0.15)';

const FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const AXIS_COLOR = '#94a3b8';
const GRID_COLOR = '#e5e7eb';

/** The design has no tooltips, no mode bar and no legends on any chart. */
export const CHART_CONFIG: Record<string, unknown> = {
    displayModeBar: false,
    staticPlot: true,
    responsive: true,
};

const BASE_XAXIS: Record<string, unknown> = {
    showgrid: false,
    zeroline: false,
    showline: false,
    ticks: '',
    fixedrange: true,
    automargin: true,
};

const BASE_YAXIS: Record<string, unknown> = {
    showgrid: true,
    gridcolor: GRID_COLOR,
    zeroline: false,
    showline: false,
    ticks: '',
    fixedrange: true,
    rangemode: 'tozero',
    nticks: 5,
    automargin: true,
};

interface ChartLayoutOverrides extends Record<string, unknown> {
    xaxis?: Record<string, unknown>;
    yaxis?: Record<string, unknown>;
}

export const chartLayout = ({
    xaxis,
    yaxis,
    ...overrides
}: ChartLayoutOverrides = {}): Record<string, unknown> => ({
    autosize: true,
    margin: { l: 40, r: 24, t: 10, b: 26 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    showlegend: false,
    font: { family: FONT_FAMILY, size: 11, color: AXIS_COLOR },
    xaxis: { ...BASE_XAXIS, ...xaxis },
    yaxis: { ...BASE_YAXIS, ...yaxis },
    ...overrides,
});

export const TICK_SPACING: Record<string, unknown> = {
    ticks: 'outside',
    ticklen: 5,
    tickcolor: 'transparent',
};

const CHART_STEP_VALUES = [1, 1.2, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8, 9, 10];

const niceCeil = (value: number): number => {
    if (!Number.isFinite(value) || value <= 0) {
        return 1;
    }
    const magnitude = 10 ** Math.floor(Math.log10(value));
    const normalized = value / magnitude;
    return (CHART_STEP_VALUES.find((step) => normalized <= step) ?? 10) * magnitude;
};

export const yRangeWithHeadroom = (values: number[], headroom = 0.15): [number, number] => {
    const max = Math.max(0, ...values);
    return max <= 0 ? [0, 1] : [0, niceCeil(max * (1 + headroom))];
};

export const categoryRange = (count: number, padding = 0.3): [number, number] => [
    -padding,
    count - 1 + padding,
];

export const seriesLabels = (series: SeriesPoint[]): string[] => series.map((point) => point.label);

export const seriesValues = (series: SeriesPoint[]): number[] => series.map((point) => point.value);
